/**
 * Motion Engine — zentrale, theme-weite Wrapper-Schicht um die echte
 * Motion-Library (motion.dev, ehem. Motion One / Framer Motion DOM-Engine),
 * per ES-Modul direkt von jsDelivr geladen. Wird NUR dort per <script>
 * eingebunden, wo tatsaechlich High-End-Motion gebraucht wird (aktuell:
 * sections/main-product-editorial.liquid, sections/style-advisor.liquid) -
 * kein globaler Import in layout/theme.liquid, um "Zero App-Bloat" auf
 * Seiten ohne diese Sections nicht zu verletzen (skills/fashion_web_design.md,
 * Abschnitt 0/1).
 *
 * Architektur: dynamischer import() (funktioniert auch in einem klassischen,
 * nicht-modularen <script defer>), damit editorial-pdp.js/style-advisor.js
 * KEINE eigenen ES-Modul-Imports mit Liquid-generierten asset_url-Pfaden
 * aufloesen muessen (Versions-Hash in Shopifys asset_url macht relative
 * Modul-Imports zwischen Assets fragil). Stattdessen: window.MotionEngine
 * (synchron pruefbar) + window.MotionEngineReady (Promise) + ein
 * 'motion-engine:ready' CustomEvent - Konsumenten koennen node antichte
 * jede der drei Formen nutzen, je nachdem ob sie schon geladen sind oder
 * noch warten.
 *
 * Bei blockiertem/fehlgeschlagenem CDN-Laden (Offline, Adblocker, CSP):
 * echter, funktionaler Fallback auf native Web Animations API mit
 * derselben Methoden-Signatur - keine stillen No-Ops, jede Methode bleibt
 * wirksam, nur ohne die Spring-Kurven-Berechnung aus der Library.
 */
(function () {
  if (window.__motionEngineInit) return;
  window.__motionEngineInit = true;

  var MOTION_CDN_URL = 'https://cdn.jsdelivr.net/npm/motion@11.18.2/+esm';
  var EASE_EXPO = [0.16, 1, 0.3, 1];
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function buildEngine(motionLib) {
    var animate = motionLib.animate;
    var spring = motionLib.spring;
    var scroll = motionLib.scroll;
    var stagger = motionLib.stagger;
    var inView = motionLib.inView;

    var SPRING_SNAPPY = spring({ stiffness: 420, damping: 28, mass: 0.9 });
    var SPRING_SOFT = spring({ stiffness: 220, damping: 24, mass: 1 });

    return {
      lib: 'motion',
      animate: animate,
      spring: spring,
      scroll: scroll,
      stagger: stagger,
      inView: inView,
      EASE_EXPO: EASE_EXPO,
      SPRING_SNAPPY: SPRING_SNAPPY,
      SPRING_SOFT: SPRING_SOFT,
      reducedMotion: reducedMotion,

      /** Echtes Spring-Rebound beim Druecken/Loslassen (ersetzt reine
       * CSS-:active-Skalierung durch physikalisch simuliertes Einsinken +
       * Ueberschwingen beim Loslassen). */
      pressFeedback: function (el) {
        if (reducedMotion || !el) return;
        var down = function () {
          animate(el, { scale: 0.94 }, { easing: SPRING_SNAPPY, duration: 0.18 });
        };
        var up = function () {
          animate(el, { scale: 1 }, { easing: SPRING_SOFT, duration: 0.5 });
        };
        el.addEventListener('pointerdown', down);
        el.addEventListener('pointerup', up);
        el.addEventListener('pointerleave', up);
        el.addEventListener('pointercancel', up);
      },

      /** Mouse-Position-Aware Glow: setzt --mx/--my (in %) auf dem Element,
       * das eigene CSS (radial-gradient(circle at var(--mx) var(--my), ...))
       * konsumiert das direkt - hier nur die Positions-Berechnung. */
      magneticGlow: function (el) {
        if (!el) return;
        el.addEventListener('pointermove', function (event) {
          var rect = el.getBoundingClientRect();
          var mx = ((event.clientX - rect.left) / rect.width) * 100;
          var my = ((event.clientY - rect.top) / rect.height) * 100;
          el.style.setProperty('--mx', mx.toFixed(1) + '%');
          el.style.setProperty('--my', my.toFixed(1) + '%');
        });
        el.addEventListener('pointerleave', function () {
          el.style.setProperty('--mx', '50%');
          el.style.setProperty('--my', '50%');
        });
      },

      /** Lens-Zoom-Magnifier: positioniert `lensEl` (fixed/absolute, per CSS
       * gestylt) exakt unter dem Cursor und setzt dessen background-image auf
       * eine hochaufgeloeste Kachel-Kopie mit passendem background-position -
       * echter optischer Lupeneffekt statt reinem Hover-Scale. */
      lensZoom: function (tile, lensEl, opts) {
        if (!tile || !lensEl) return;
        var zoom = (opts && opts.zoom) || 2.2;
        var lensSize = (opts && opts.size) || 180;
        var img = tile.querySelector('img');
        if (!img) return;

        var move = function (event) {
          var rect = tile.getBoundingClientRect();
          var x = event.clientX - rect.left;
          var y = event.clientY - rect.top;
          var px = (x / rect.width) * 100;
          var py = (y / rect.height) * 100;

          lensEl.style.backgroundImage = 'url("' + (img.currentSrc || img.src) + '")';
          lensEl.style.backgroundSize = rect.width * zoom + 'px ' + rect.height * zoom + 'px';
          lensEl.style.backgroundPosition = px + '% ' + py + '%';
          lensEl.style.transform =
            'translate3d(' + (x - lensSize / 2) + 'px, ' + (y - lensSize / 2) + 'px, 0)';
        };

        var show = function (event) {
          tile.setAttribute('data-lens-active', '');
          lensEl.hidden = false;
          move(event);
          if (!reducedMotion) {
            animate(lensEl, { opacity: [0, 1] }, { duration: 0.22, easing: EASE_EXPO });
          } else {
            lensEl.style.opacity = 1;
          }
        };

        var hide = function () {
          tile.removeAttribute('data-lens-active');
          var done = function () {
            lensEl.hidden = true;
          };
          if (!reducedMotion) {
            animate(lensEl, { opacity: [1, 0] }, { duration: 0.18, easing: EASE_EXPO }).finished.then(done);
          } else {
            lensEl.style.opacity = 0;
            done();
          }
        };

        tile.addEventListener('pointerenter', show);
        tile.addEventListener('pointermove', move);
        tile.addEventListener('pointerleave', hide);
      },

      /** Scroll-gebundene Parallax-Verschiebung ueber Motions eigene
       * scroll()-Engine (kompositiert, kein rAF-Scroll-Listener noetig) -
       * echtes "Scroll-Observer"-Feature statt manueller Berechnung.
       * Animiert bewusst NUR die CSS-Custom-Property --parallax-offset auf
       * `el` (nicht `transform` direkt) - das eigentliche transform bleibt
       * vollstaendig in CSS definiert (translateY(var(--parallax-offset))
       * scale(...)), damit diese Scroll-Animation nicht mit der separaten,
       * CSS-transition-basierten Hover-Zoom-Animation auf transform
       * kollidiert (beide wuerden sonst dieselbe Property gegenseitig
       * ueberschreiben). --parallax-offset vererbt sich per CSS-Kaskade an
       * das <img>-Kind, das den eigentlichen Wert konsumiert. */
      scrollParallax: function (el, opts) {
        if (reducedMotion || !el || !scroll) return null;
        var distancePx = (opts && opts.distancePx) || 18;
        return scroll(
          animate(el, { '--parallax-offset': [-distancePx + 'px', distancePx + 'px'] }, { easing: 'linear' }),
          { target: el, offset: ['start end', 'end start'] }
        );
      },

      /** Fuer Modal-Panels: spring-basiertes Einfliegen der Kinder-Elemente,
       * getriggert wenn Dawns <modal-dialog> das 'open'-Attribut setzt (siehe
       * Aufrufer). Nur die "In"-Richtung wird animiert - "Out" bleibt Dawns
       * eigener, synchroner hide()-Logik ueberlassen (kein Race-Risiko). */
      panelEntrance: function (panelEl) {
        if (reducedMotion || !panelEl) return;
        var children = panelEl.querySelectorAll(':scope > *');
        if (!children.length) {
          animate(panelEl, { opacity: [0, 1], transform: ['scale(0.96)', 'scale(1)'] }, { easing: SPRING_SOFT, duration: 0.5 });
          return;
        }
        animate(
          children,
          { opacity: [0, 1], transform: ['translateY(14px)', 'translateY(0)'] },
          { delay: stagger(0.05), easing: EASE_EXPO, duration: 0.5 }
        );
      },

      /** Stroke-Draw-On-Animation fuer den Erfolgs-Checkmark (SVG-Path) -
       * echte numerische WAAPI-Animation von stroke-dashoffset. */
      drawCheckmark: function (pathEl) {
        if (!pathEl) return;
        var length = pathEl.getTotalLength ? pathEl.getTotalLength() : 24;
        pathEl.style.strokeDasharray = String(length);
        pathEl.style.strokeDashoffset = String(length);
        if (reducedMotion) {
          pathEl.style.strokeDashoffset = '0';
          return;
        }
        animate(pathEl, { strokeDashoffset: [length, 0] }, { duration: 0.45, easing: EASE_EXPO });
      },
    };
  }

  /** Funktionaler Fallback (kein CDN-Zugriff): dieselbe Methoden-Signatur,
   * realisiert mit nativer Element.animate() (WAAPI) + Expo-Bezier statt
   * echter Feder-Physik - degradiert die Qualitaet, nicht die Funktion. */
  function buildFallbackEngine() {
    var easeCss = 'cubic-bezier(' + EASE_EXPO.join(',') + ')';

    function nativeAnimate(el, keyframesObj, opts) {
      var keyframes = {};
      Object.keys(keyframesObj).forEach(function (prop) {
        keyframes[prop === 'scale' ? 'transform' : prop] =
          prop === 'scale'
            ? [].concat(keyframesObj[prop]).map(function (v) {
                return 'scale(' + v + ')';
              })
            : keyframesObj[prop];
      });
      var anim = el.animate(keyframes, {
        duration: ((opts && opts.duration) || 0.3) * 1000,
        easing: easeCss,
        fill: 'forwards',
      });
      return { finished: anim.finished || Promise.resolve() };
    }

    return {
      lib: 'fallback-waapi',
      animate: nativeAnimate,
      spring: function () {
        return easeCss;
      },
      scroll: null,
      stagger: function (step) {
        return function (i) {
          return i * step;
        };
      },
      inView: null,
      EASE_EXPO: EASE_EXPO,
      reducedMotion: reducedMotion,
      pressFeedback: function (el) {
        if (reducedMotion || !el) return;
        el.addEventListener('pointerdown', function () {
          nativeAnimate(el, { scale: [1, 0.94] }, { duration: 0.15 });
        });
        var release = function () {
          nativeAnimate(el, { scale: [0.94, 1] }, { duration: 0.25 });
        };
        el.addEventListener('pointerup', release);
        el.addEventListener('pointerleave', release);
      },
      magneticGlow: function (el) {
        if (!el) return;
        el.addEventListener('pointermove', function (event) {
          var rect = el.getBoundingClientRect();
          el.style.setProperty('--mx', (((event.clientX - rect.left) / rect.width) * 100).toFixed(1) + '%');
          el.style.setProperty('--my', (((event.clientY - rect.top) / rect.height) * 100).toFixed(1) + '%');
        });
        el.addEventListener('pointerleave', function () {
          el.style.setProperty('--mx', '50%');
          el.style.setProperty('--my', '50%');
        });
      },
      lensZoom: function (tile, lensEl, opts) {
        if (!tile || !lensEl) return;
        var zoom = (opts && opts.zoom) || 2.2;
        var lensSize = (opts && opts.size) || 180;
        var img = tile.querySelector('img');
        if (!img) return;
        var move = function (event) {
          var rect = tile.getBoundingClientRect();
          var x = event.clientX - rect.left;
          var y = event.clientY - rect.top;
          lensEl.style.backgroundImage = 'url("' + (img.currentSrc || img.src) + '")';
          lensEl.style.backgroundSize = rect.width * zoom + 'px ' + rect.height * zoom + 'px';
          lensEl.style.backgroundPosition = (x / rect.width) * 100 + '% ' + (y / rect.height) * 100 + '%';
          lensEl.style.transform = 'translate3d(' + (x - lensSize / 2) + 'px, ' + (y - lensSize / 2) + 'px, 0)';
        };
        tile.addEventListener('pointerenter', function (event) {
          tile.setAttribute('data-lens-active', '');
          lensEl.hidden = false;
          lensEl.style.opacity = 1;
          move(event);
        });
        tile.addEventListener('pointermove', move);
        tile.addEventListener('pointerleave', function () {
          tile.removeAttribute('data-lens-active');
          lensEl.style.opacity = 0;
          lensEl.hidden = true;
        });
      },
      scrollParallax: function () {
        return null;
      },
      panelEntrance: function (panelEl) {
        if (reducedMotion || !panelEl) return;
        nativeAnimate(panelEl, { opacity: [0, 1] }, { duration: 0.35 });
      },
      drawCheckmark: function (pathEl) {
        if (!pathEl) return;
        var length = pathEl.getTotalLength ? pathEl.getTotalLength() : 24;
        pathEl.style.strokeDasharray = String(length);
        pathEl.style.strokeDashoffset = reducedMotion ? '0' : String(length);
        if (!reducedMotion) {
          nativeAnimate(pathEl, { strokeDashoffset: [length, 0] }, { duration: 0.45 });
        }
      },
    };
  }

  window.MotionEngineReady = import(/* webpackIgnore: true */ MOTION_CDN_URL)
    .then(function (motionLib) {
      window.MotionEngine = buildEngine(motionLib);
      window.dispatchEvent(new CustomEvent('motion-engine:ready', { detail: window.MotionEngine }));
      return window.MotionEngine;
    })
    .catch(function (error) {
      console.warn('[MotionEngine] CDN-Laden fehlgeschlagen, nutze funktionalen WAAPI-Fallback.', error);
      window.MotionEngine = buildFallbackEngine();
      window.dispatchEvent(new CustomEvent('motion-engine:ready', { detail: window.MotionEngine }));
      return window.MotionEngine;
    });

  /** Bequeme Callback-API fuer Konsumenten, die weder Promise noch Event
   * Handling selbst schreiben wollen. */
  window.whenMotionReady = function (callback) {
    if (window.MotionEngine) {
      callback(window.MotionEngine);
    } else {
      window.MotionEngineReady.then(callback);
    }
  };
})();
